<!--@format-->
<template>
    <div v-cloak class="report-schedule-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>{{ title }}</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <loading-card :is-loading="isLoading" />
            <div class="content-wrapper">
                <div :class="['field', { 'field-error': hasTypeError }]">
                    <label>{{ reportInputLabel }}</label>
                    <app-select-dropdown
                        v-model="report.type"
                        :disabled="isEditMode"
                        :items="reportTypes"
                        placeholder="Report Formats"
                        @input="handleInputReportType"
                    />
                    <span v-if="hasTypeError" class="description">Selection is required</span>
                </div>
                <div :class="['field', { 'field-error': hasFrequencyError }]">
                    <label>Frequency</label>
                    <app-select-dropdown
                        v-model="report.frequency"
                        :is-custom-item="true"
                        :is-custom-selection="true"
                        :items="reportFrequencyTypes"
                        placeholder="Select Frequency"
                    >
                        <template #selection="{ item }">
                            <span class="item-title-name">{{ item.name }}:&nbsp;</span>
                            {{ item.details }}
                        </template>
                        <template #item="{ item }">
                            <span class="item-title-name">{{ item.name }}:&nbsp;</span>
                            {{ item.details }}
                        </template>
                    </app-select-dropdown>
                    <span v-if="hasFrequencyError" class="description">Selection is required</span>
                </div>
                <div :class="['field', { 'field-error': hasFormatError }]">
                    <div class="section-label">Select Report Format</div>
                    <div class="section-container">
                        <div
                            v-for="({ label, disabled }, idx) in reportFormats"
                            :key="idx"
                            class="format-checkbox-container"
                        >
                            <div v-if="!disabled" class="format-checkbox">
                                <app-checkbox
                                    v-model="reportFormats[idx].value"
                                    :disabled="isLoading"
                                    @input="handleInputReportFormat"
                                />
                                {{ label }}
                            </div>
                        </div>
                    </div>
                    <span v-if="hasFormatError" class="description">Please make a selection</span>
                </div>
                <div :class="['field', { 'field-error': hasRecipientError }]">
                    <div class="section-label">Recipients</div>
                    <div class="section-container">
                        <div>
                            <label>Who will receive this report?</label>
                            <app-input-list
                                :options="reportRecipients"
                                @update:menuItems="handleUpdateRecipients"
                            />
                        </div>
                    </div>
                    <span v-if="hasRecipientError" class="description">
                        Please enter valid email addresses only
                    </span>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="cancel-btn" @click="closeWidget"> Cancel </app-button>
                </div>
                <div class="action-container">
                    <app-button
                        :disabled="isLoading || $v.$anyError"
                        :loading="isSubmitting"
                        @click="handleClickScheduleReport"
                    >
                        {{ actionButtonLabel }}
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { v4 as uuidv4 } from 'uuid';
import { ReportsScheduledWidgetServiceMixin } from '@/mixins/widget/reports-scheduled-widget-service-mixin';
import EventBus from '@/scripts/event-bus.js';
import { email, minLength, required } from 'vuelidate/lib/validators';

const AppButton = () => import('@/components/common/app-button.vue');
const AppCheckbox = () => import('@/components/common/app-checkbox');
const AppInputList = () => import('@/components/common/app-input-list');
const AppSelectDropdown = () => import('@/components/common/app-select-dropdown');
const LoadingCard = () => import('@/components/common/loading-card');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');

export default {
    name: 'report-schedule-widget',

    components: {
        'app-button': AppButton,
        'app-checkbox': AppCheckbox,
        'app-input-list': AppInputList,
        'app-select-dropdown': AppSelectDropdown,
        'loading-card': LoadingCard,
        'widget-close-svg': WidgetCloseSvg,
    },

    mixins: [ReportsScheduledWidgetServiceMixin],

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isLoading: false,

            isSubmitting: false,

            reportTypes: [],

            reportFrequencyTypes: [],

            reportFormats: [
                {
                    disabled: false,
                    id: 1,
                    label: 'In-App',
                    value: false,
                },
                {
                    disabled: false,
                    id: 2,
                    label: 'PDF',
                    value: false,
                },
                {
                    disabled: false,
                    id: 3,
                    label: 'Excel',
                    value: false,
                },
            ],

            reportRecipients: [
                {
                    id: uuidv4(),
                    name: '',
                },
            ],

            report: {
                type: null,
                frequency: null,
                format: [],
                recipients: [],
            },
        };
    },

    validations() {
        return {
            report: {
                type: {
                    required,
                },

                frequency: {
                    required,
                },

                format: {
                    required,
                    minLength: minLength(1),
                },

                recipients: {
                    required,
                    $each: {
                        required,
                        email,
                    },
                },
            },
        };
    },

    computed: {
        accountId() {
            return this.widget.accountId;
        },

        isEditMode() {
            return (
                this.widget.scheduledReportId !== null &&
                this.widget.scheduledReportId !== undefined
            );
        },

        scheduledReportId() {
            return this.widget.scheduledReportId;
        },

        title() {
            return this.isEditMode ? 'Edit a Scheduled Report' : 'Schedule a Report';
        },

        reportInputLabel() {
            return this.isEditMode ? 'Report' : 'Reports';
        },

        actionButtonLabel() {
            return this.isEditMode ? 'Save' : 'Schedule';
        },

        hasTypeError() {
            return this.$v.report.type.$error;
        },

        hasFrequencyError() {
            return this.$v.report.frequency.$error;
        },

        hasFormatError() {
            return this.$v.report.format.$error;
        },

        hasRecipientError() {
            return this.$v.report.recipients.$error;
        },
    },

    watch: {
        reportRecipients: {
            deep: true,
            handler() {
                this.report.recipients = this.reportRecipients.map(({ name }) => name);
            },
        },
    },

    created() {
        const getDataProcess = () => {
            return new Promise((resolve) => {
                this.initializeReportData()
                    .then(() => {
                        if (this.isEditMode) {
                            return this.getScheduledReportData();
                        }
                    })
                    .then(resolve)
                    .catch(this.closeWidget);
            });
        };

        this.withLoadingBoundary(getDataProcess);
    },

    methods: {
        initializeReportData() {
            return new Promise((resolve, reject) =>
                Promise.all([this.getReportTypes(), this.getReportFrequencyTypes()])
                    .then(resolve)
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while fetching reports information.',
                            { error },
                        );
                        reject();
                    })
            );
        },

        getScheduledReportData() {
            const config = {
                filter: `Id eq ${this.scheduledReportId}`,
            };
            return this.dispatchGetScheduledReports({
                config,
                accountId: this.accountId,
            })
                .then((value) => {
                    const [scheduledReport] = value;
                    const {
                        ReportTypeId,
                        ReportFrequencyTypeId,
                        InApp,
                        Pdf,
                        Excel,
                        Recipients,
                    } = scheduledReport;

                    const [reportType] = this.reportTypes.filter(({ id }) => id === ReportTypeId);
                    const [reportFrequency] = this.reportFrequencyTypes.filter(
                        ({ id }) => id === ReportFrequencyTypeId
                    );

                    const isOverallReport =
                        reportType.name === 'Overall Marketing Report' ||
                        reportType.name === 'Overall Sales Report';

                    this.reportFormats[0].value = InApp;
                    this.reportFormats[0].disabled = isOverallReport;
                    this.reportFormats[1].value = Pdf;
                    this.reportFormats[2].value = Excel;

                    if (Recipients) {
                        this.reportRecipients = JSON.parse(Recipients).map((recipient) => ({
                            id: uuidv4(),
                            name: recipient,
                        }));
                    }

                    this.report.type = reportType;
                    this.report.frequency = reportFrequency;
                    this.report.format = this.reportFormats.filter(({ value }) => value);
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while fetching your scheduled report information.',
                        { error },
                    );
                });
        },

        getReportTypes() {
            return this.dispatchGetReportTypes(this.widget.tags).then((items) => {
                this.reportTypes = items;
            });
        },

        getReportFrequencyTypes() {
            return this.dispatchGetReportFrequencyTypes().then((items) => {
                this.reportFrequencyTypes = items;
            });
        },

        handleInputReportType() {
            const reportName = this.report.type.name;
            const isOverallReport =
                reportName === 'Overall Marketing Report' || reportName === 'Overall Sales Report';

            this.report.format = [];
            this.reportFormats = this.reportFormats.map((reportFormat) => {
                const isInApp = reportFormat.label === 'In-App';
                const isFormatDisabled = isInApp && isOverallReport;

                return {
                    ...reportFormat,
                    value: false,
                    disabled: isFormatDisabled,
                };
            });
        },

        handleInputReportFormat() {
            this.report.format = this.reportFormats.filter(({ value }) => value);
        },

        handleUpdateRecipients(recipients) {
            this.reportRecipients = recipients;
        },

        handleClickScheduleReport() {
            this.$v.$touch();
            if (!this.$v.$anyError) {
                const handlerFunction = this.isEditMode
                    ? this.handleUpdateScheduledReport
                    : this.handleCreateScheduledReport;

                this.isSubmitting = true;

                this.withLoadingBoundary(() =>
                    handlerFunction().finally(() => (this.isSubmitting = false))
                );
            }
        },

        handleUpdateScheduledReport() {
            const changes = [
                {
                    name: 'ReportTypeId',
                    value: this.report.type.id,
                },
                {
                    name: 'ReportFrequencyTypeId',
                    value: this.report.frequency.id,
                },
                {
                    name: 'InApp',
                    value: this.report.format.some(({ id }) => id === 1),
                },
                {
                    name: 'Pdf',
                    value: this.report.format.some(({ id }) => id === 2),
                },
                {
                    name: 'Excel',
                    value: this.report.format.some(({ id }) => id === 3),
                },
                {
                    name: 'Recipients',
                    value: this.report.recipients,
                },
            ];

            return this.updateScheduledReport(this.scheduledReportId, changes)
                .then(() => {
                    this.$helpers.showSuccessNotification(
                        `<strong>${this.report.type.name}</strong> was updated successfully.`
                    );
                    this.closeWidget();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('Failed to update the scheduled report.', { error });
                });
        },

        handleCreateScheduledReport() {
            const payload = {
                ReportTypeId: this.report.type.id,
                ReportFrequencyTypeId: this.report.frequency.id,
                InApp: this.report.format.some(({ id }) => id === 1),
                Pdf: this.report.format.some(({ id }) => id === 2),
                Excel: this.report.format.some(({ id }) => id === 3),
                Recipients: this.report.recipients,
                AccountId: this.accountId,
            };

            return this.createScheduledReport(payload)
                .then(() => {
                    this.$helpers.showSuccessNotification(
                        `<strong>${this.report.type.name}</strong> was scheduled successfully.`
                    );
                    this.closeWidget();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while scheduling your report.',
                        { error },
                    );
                });
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        withLoadingBoundary(promise) {
            this.isLoading = true;
            promise().finally(() => {
                this.isLoading = false;
            });
        },
    },
};
</script>

<style>
.report-schedule-page .v-list {
    padding: 0;
}

.report-schedule-page
    .v-select:not(.v-select--is-multi).v-text-field--single-line
    .v-select__selections {
    color: #382153;
    font-size: 16px;
}

.report-schedule-page .v-list-item:not(.v-list-item--active):not(.v-list-item--disabled),
.report-schedule-page
    .v-list-item:not(.v-list-item--active):not(.v-list-item--disabled)
    .v-list-item__title {
    font-size: 14px;
    color: #382153;
}

.report-schedule-page .v-list-item--active {
    background-color: #dbecfa;
    color: #18aeff;
}

.report-schedule-page .v-list-item--active,
.report-schedule-page .v-list-item--active .v-list-item__title {
    font-size: 14px;
}

.report-schedule-page .v-list-item--disabled {
    color: #877d88;
}

.report-schedule-page .v-list-item .item-title-name,
.report-schedule-page .v-select__selections .item-title-name {
    font-weight: bold;
    white-space: pre-wrap;
}

.report-schedule-widget .v-select__selection--disabled {
    color: #382153 !important;
}

.report-schedule-widget .widget-contents-container .content-wrapper {
    display: flex;
    flex-direction: column;
    gap: 22px;
    padding: 14px 27px;
}

.report-schedule-widget .v-input--is-disabled .v-input__icon {
    display: none;
}

.report-schedule-widget label {
    color: #382153;
}

.report-schedule-widget .section-label {
    background: #ffffff;
    bottom: -12px;
    color: #382153;
    font-weight: 500;
    margin: 0 auto;
    padding: 0 47px;
    position: relative;
    width: fit-content;
}

.report-schedule-widget .section-container {
    border-radius: 22px;
    border: 2px solid #e9e9ef;
    display: flex;
    flex-direction: column;
    gap: 30px;
    padding: 22px 32px;
}

.report-schedule-widget .section-container .format-checkbox-container:empty {
    display: none;
}

.report-schedule-widget .format-checkbox {
    display: flex;
    gap: 16px;
}

.report-schedule-widget .app-input-list .app-button.add-btn {
    background-color: #18aeff;
}

.report-schedule-widget .app-input-list .app-button.add-btn svg path {
    fill: #ffffff;
}

.report-schedule-widget .field-error label,
.report-schedule-widget .field-error .section-label,
.report-schedule-widget .field-error .description {
    color: #f30070;
}

.report-schedule-widget .field-error fieldset,
.report-schedule-widget .field-error .section-container {
    border-color: #f30070;
}

.report-schedule-widget .field-error .description {
    font-size: 12px;
}

.report-schedule-widget .cancel-btn {
    background: transparent;
    color: #877d88;
    border: none;
}

.report-schedule-widget .cancel-btn:hover {
    background: #e1e0e5;
}

.report-schedule-widget .widget-contents-container .loading-screen {
    margin-left: 0;
}
</style>
